/**
 * kernels — TSL compute kernels for GPU particle spawn + update, plus the
 * shared GPU storage buffers.
 *
 * Buffers (SoA, three vec4 storage arrays — see types.ts for the layout):
 *   posBuffer  : vec4(px, py, pz, age)
 *   velBuffer  : vec4(vx, vy, vz, size)
 *   miscBuffer : vec4(life, seed, startSize, startSpeed)
 *
 * The spawn kernel initialises a CONTIGUOUS slot range `[uSpawnStart,
 * uSpawnStart + uSpawnCount)` (the CPU ring-buffer hands it one range per
 * dispatch). Per-particle randomness comes from `hash(instanceIndex +
 * frameSeed)` — never Math.random — so it's reproducible and GPU-side.
 *
 * The update kernel ages every particle, parks the dead ones far away with
 * size 0 (so the vertex stage culls them), then applies gravity, drag,
 * optional curl-noise turbulence, and an optional attractor.
 *
 * r182 notes:
 *   • `instancedArray(count, type)` has a known off-by-one in `.bufferCount`
 *     (fixed r183) — we over-allocate one slot, mirroring RainComputeEffect.
 *   • `instancedArray` exposes `.element(idx)` (compute read/write) and
 *     `.toAttribute()` (vertex-stage read). Verified against three/tsl r182.
 *   • Compute `Fn(() => {...})().compute(count)` is the established dispatch
 *     form (see crowd/poseEval.compute.ts, render/visibility/CullLODCompute).
 */

import {
  instancedArray,
  instanceIndex,
  hash,
  deltaTime,
  Fn,
  uniform,
  vec3,
  vec4,
  float,
  uint,
  If,
  texture3D,
} from 'three/tsl';
import type * as THREE from 'three/webgpu';
import {
  GPU_PARTICLE_DEAD_Y,
  GpuShapeCode,
} from './types.js';
import {
  POTENTIAL_SEED_X,
  POTENTIAL_SEED_Y,
  POTENTIAL_SEED_Z,
  CURL_EPSILON,
} from './curlNoise.js';

// ─── Public handles ──────────────────────────────────────────────────────────

/** Uniforms the CPU mutates per-frame / per-emit on the spawn kernel. */
export interface SpawnUniforms {
  spawnStart: ReturnType<typeof uniform>;
  spawnCount: ReturnType<typeof uniform>;
  frameSeed: ReturnType<typeof uniform>;
  // Emitter transform origin (world-space simulation places spawns here).
  originX: ReturnType<typeof uniform>;
  originY: ReturnType<typeof uniform>;
  originZ: ReturnType<typeof uniform>;
  // Emitter BASIS — the world matrix's upper 3×3, one uniform per column
  // component (the local convention for vectors in this file; cf. originX/Y/Z,
  // boxX/Y/Z). Spawn positions and directions come out of the shape samplers in
  // emitter-LOCAL space, so without this they were only translated: a rotated
  // emitter emitted as if it were axis-aligned. The CPU reference
  // (ParticleEmitter.emitOne, simulationSpace 'world') applies the full matrix
  // to the position and `transformDirection` to the velocity — this mirrors it.
  basis0X: ReturnType<typeof uniform>; basis0Y: ReturnType<typeof uniform>; basis0Z: ReturnType<typeof uniform>;
  basis1X: ReturnType<typeof uniform>; basis1Y: ReturnType<typeof uniform>; basis1Z: ReturnType<typeof uniform>;
  basis2X: ReturnType<typeof uniform>; basis2Y: ReturnType<typeof uniform>; basis2Z: ReturnType<typeof uniform>;
  shape: ReturnType<typeof uniform>;
  shapeRadius: ReturnType<typeof uniform>;
  shapeHeight: ReturnType<typeof uniform>;
  shapeAngle: ReturnType<typeof uniform>;
  boxX: ReturnType<typeof uniform>;
  boxY: ReturnType<typeof uniform>;
  boxZ: ReturnType<typeof uniform>;
  speedMin: ReturnType<typeof uniform>;
  speedMax: ReturnType<typeof uniform>;
  lifeMin: ReturnType<typeof uniform>;
  lifeMax: ReturnType<typeof uniform>;
  sizeMin: ReturnType<typeof uniform>;
  sizeMax: ReturnType<typeof uniform>;
}

/** Uniforms the CPU mutates per-frame on the update kernel. */
export interface UpdateUniforms {
  gravity: ReturnType<typeof uniform>;
  drag: ReturnType<typeof uniform>;
  curlStrength: ReturnType<typeof uniform>;
  curlFrequency: ReturnType<typeof uniform>;
  curlScroll: ReturnType<typeof uniform>;
  attractorX: ReturnType<typeof uniform>;
  attractorY: ReturnType<typeof uniform>;
  attractorZ: ReturnType<typeof uniform>;
  attractorStrength: ReturnType<typeof uniform>;
  time: ReturnType<typeof uniform>;
}

/** The full GPU particle buffer + kernel bundle. */
export interface ParticleKernels {
  /** vec4(px,py,pz,age) — read in the vertex stage via `.toAttribute()`. */
  posBuffer: ReturnType<typeof instancedArray>;
  /** vec4(vx,vy,vz,size). */
  velBuffer: ReturnType<typeof instancedArray>;
  /** vec4(life,seed,startSize,startSpeed). */
  miscBuffer: ReturnType<typeof instancedArray>;
  /** Compute node that zeroes all slots (run once at create). */
  clearNode: unknown;
  /** Compute node that spawns a contiguous batch. */
  spawnNode: unknown;
  /** Compute node that ages + integrates every particle. */
  updateNode: unknown;
  spawnUniforms: SpawnUniforms;
  updateUniforms: UpdateUniforms;
}

// ─── TSL value-noise + curl (mirror of curlNoise.ts) ─────────────────────────

/**
 * Integer hash → float in [0,1), mirroring `hash3` in curlNoise.ts using TSL
 * uint bit ops. Lattice coords arrive as TSL int/uint nodes.
 */
// WHY plain functions (NOT `Fn(([args]) => …)`): these build their node graph INLINE
// at each call site. The previous Fn-wrapped form failed WGSL codegen when nested 3-deep
// (curl → valueNoise3 → fade/hash3) INSIDE the update kernel's `.Else()` branch — the
// inline-Fn parameter substitution dropped the args, emitting `[object Object]` operands
// into the generated WGSL → an invalid compute pipeline → a black frame (the whole frame
// submit is poisoned). Inlining removes the parameter binding entirely. This matches
// `sampleShapePos`/`sampleShapeDir` below, which were already plain functions. Do NOT
// re-wrap these in `Fn` without an explicit type layout — it reintroduces the crash.
function hash3TSL(ix: any, iy: any, iz: any, seed: any): any {
  // Match curlNoise.ts: ix*374761393 + iy*668265263 + iz*2147483647 + seed*1274126177
  let h: any = uint(ix).mul(uint(374761393))
    .add(uint(iy).mul(uint(668265263)))
    .add(uint(iz).mul(uint(2147483647)))
    .add(uint(seed).mul(uint(1274126177)));
  h = h.bitXor(h.shiftRight(uint(13)));
  h = h.mul(uint(1274126177));
  h = h.bitXor(h.shiftRight(uint(16)));
  // top 24 bits → [0,1)
  return float(h.shiftRight(uint(8))).div(float(16777216.0));
}

/** Quintic fade 6t⁵−15t⁴+10t³. */
function fadeTSL(t: any): any {
  const tt = float(t);
  return tt.mul(tt).mul(tt).mul(tt.mul(tt.mul(6.0).sub(15.0)).add(10.0));
}

/**
 * Trilinear value noise in [0,1] at (x,y,z) for `seed`. Mirrors
 * `valueNoise3`. Floors the coords to integer lattice via `.floor()` then
 * casts to uint for the hash (positive domain assumed for turbulence; the
 * noise is periodic enough that the sign convention is cosmetic).
 */
function valueNoise3TSL(x: any, y: any, z: any, seed: any): any {
  const x0f = float(x).floor();
  const y0f = float(y).floor();
  const z0f = float(z).floor();
  const fx = float(x).sub(x0f);
  const fy = float(y).sub(y0f);
  const fz = float(z).sub(z0f);
  const ux = fadeTSL(fx);
  const uy = fadeTSL(fy);
  const uz = fadeTSL(fz);

  // Lattice corner indices as uint. A large positive float bias is added
  // BEFORE the cast so negative world coordinates produce a non-negative
  // operand (the WGSL `u32(f32)` cast is only well-defined for non-negative
  // finite values). The same bias is applied to all axes so relative lattice
  // structure is preserved. The CPU reference uses signed-int wraparound
  // instead; the two share the same lattice topology + fade + value-noise
  // shape (so turbulence looks identical) but are NOT bit-identical — which
  // is fine, GPU/CPU float noise never matches bit-for-bit anyway.
  const LATTICE_BIAS = float(1 << 20);
  const ix0 = uint(x0f.add(LATTICE_BIAS));
  const iy0 = uint(y0f.add(LATTICE_BIAS));
  const iz0 = uint(z0f.add(LATTICE_BIAS));
  const ix1 = ix0.add(uint(1));
  const iy1 = iy0.add(uint(1));
  const iz1 = iz0.add(uint(1));
  const s = uint(seed);

  const c000 = hash3TSL(ix0, iy0, iz0, s);
  const c100 = hash3TSL(ix1, iy0, iz0, s);
  const c010 = hash3TSL(ix0, iy1, iz0, s);
  const c110 = hash3TSL(ix1, iy1, iz0, s);
  const c001 = hash3TSL(ix0, iy0, iz1, s);
  const c101 = hash3TSL(ix1, iy0, iz1, s);
  const c011 = hash3TSL(ix0, iy1, iz1, s);
  const c111 = hash3TSL(ix1, iy1, iz1, s);

  const x00 = c000.add(c100.sub(c000).mul(ux));
  const x10 = c010.add(c110.sub(c010).mul(ux));
  const x01 = c001.add(c101.sub(c001).mul(ux));
  const x11 = c011.add(c111.sub(c011).mul(ux));
  const y0i = x00.add(x10.sub(x00).mul(uy));
  const y1i = x01.add(x11.sub(x01).mul(uy));
  return y0i.add(y1i.sub(y0i).mul(uz));
}

/**
 * Curl-noise velocity at p via central differences (mirror of `curlNoise`).
 * Returns a vec3 node. `h` is the finite-difference epsilon.
 */
function curlNoiseTSL(px: any, py: any, pz: any): any {
  const h = float(CURL_EPSILON);
  const inv2h = float(1.0).div(h.mul(2.0));
  const sx = uint(POTENTIAL_SEED_X);
  const sy = uint(POTENTIAL_SEED_Y);
  const sz = uint(POTENTIAL_SEED_Z);

  // vx = ∂Pz/∂y − ∂Py/∂z
  const pZ_yp = valueNoise3TSL(px, float(py).add(h), pz, sz);
  const pZ_yn = valueNoise3TSL(px, float(py).sub(h), pz, sz);
  const pY_zp = valueNoise3TSL(px, py, float(pz).add(h), sy);
  const pY_zn = valueNoise3TSL(px, py, float(pz).sub(h), sy);
  const vx = pZ_yp.sub(pZ_yn).mul(inv2h).sub(pY_zp.sub(pY_zn).mul(inv2h));

  // vy = ∂Px/∂z − ∂Pz/∂x
  const pX_zp = valueNoise3TSL(px, py, float(pz).add(h), sx);
  const pX_zn = valueNoise3TSL(px, py, float(pz).sub(h), sx);
  const pZ_xp = valueNoise3TSL(float(px).add(h), py, pz, sz);
  const pZ_xn = valueNoise3TSL(float(px).sub(h), py, pz, sz);
  const vy = pX_zp.sub(pX_zn).mul(inv2h).sub(pZ_xp.sub(pZ_xn).mul(inv2h));

  // vz = ∂Py/∂x − ∂Px/∂y
  const pY_xp = valueNoise3TSL(float(px).add(h), py, pz, sy);
  const pY_xn = valueNoise3TSL(float(px).sub(h), py, pz, sy);
  const pX_yp = valueNoise3TSL(px, float(py).add(h), pz, sx);
  const pX_yn = valueNoise3TSL(px, float(py).sub(h), pz, sx);
  const vz = pY_xp.sub(pY_xn).mul(inv2h).sub(pX_yp.sub(pX_yn).mul(inv2h));

  return vec3(vx, vy, vz);
}

// ─── Spawn-shape sampling ────────────────────────────────────────────────────
//
// Each shape consumes a handful of hash() draws keyed off (instanceIndex,
// frameSeed). Direction + position are derived per the CPU emitter's shapes.

/** Sample a local spawn position for a shape code. Returns vec3 node. */
function sampleShapePos(u: SpawnUniforms, h: any[]): any {
  // h[0..5] are independent [0,1) draws.
  const radius = float(u.shapeRadius);
  const r = radius.mul(h[0].pow(float(1.0 / 3.0))); // cbrt for volume shapes

  // sphere/hemisphere direction
  const theta = h[1].mul(float(Math.PI * 2));
  const cosPhi = h[2].mul(2.0).sub(1.0);
  const sinPhi = cosPhi.mul(cosPhi).oneMinus().max(0.0).sqrt();
  const spherePos = vec3(
    r.mul(sinPhi).mul(theta.cos()),
    r.mul(sinPhi).mul(theta.sin()),
    r.mul(cosPhi),
  );
  const hemiCos = h[2]; // [0,1) → upper hemisphere
  const hemiSin = hemiCos.mul(hemiCos).oneMinus().max(0.0).sqrt();
  const hemiPos = vec3(
    r.mul(hemiSin).mul(theta.cos()),
    r.mul(hemiCos),
    r.mul(hemiSin).mul(theta.sin()),
  );
  const boxPos = vec3(
    h[0].sub(0.5).mul(2.0).mul(float(u.boxX)),
    h[1].sub(0.5).mul(2.0).mul(float(u.boxY)),
    h[2].sub(0.5).mul(2.0).mul(float(u.boxZ)),
  );
  const coneR = radius.mul(h[0].sqrt());
  const conePos = vec3(
    coneR.mul(theta.cos()),
    h[3].mul(float(u.shapeHeight)),
    coneR.mul(theta.sin()),
  );
  const circR = radius.mul(h[0].sqrt());
  const circlePos = vec3(circR.mul(theta.cos()), float(0.0), circR.mul(theta.sin()));

  // Select by shape code via nested .select() (ternary-style).
  const code = u.shape;
  return float(code).equal(float(GpuShapeCode.Sphere)).select(
    spherePos,
    float(code).equal(float(GpuShapeCode.Hemisphere)).select(
      hemiPos,
      float(code).equal(float(GpuShapeCode.Box)).select(
        boxPos,
        float(code).equal(float(GpuShapeCode.Cone)).select(
          conePos,
          float(code).equal(float(GpuShapeCode.Circle)).select(
            circlePos,
            vec3(0.0, 0.0, 0.0), // Point
          ),
        ),
      ),
    ),
  );
}

/** Sample a local unit emission direction for a shape code. Returns vec3. */
function sampleShapeDir(u: SpawnUniforms, h: any[]): any {
  const theta = h[4].mul(float(Math.PI * 2));
  // cone: uniform solid-angle around +Y within shapeAngle
  const cosMax = float(u.shapeAngle).cos();
  const cosPhi = cosMax.add(float(1.0).sub(cosMax).mul(h[5]));
  const sinPhi = cosPhi.mul(cosPhi).oneMinus().max(0.0).sqrt();
  const coneDir = vec3(sinPhi.mul(theta.cos()), cosPhi, sinPhi.mul(theta.sin())).normalize();

  // sphere: any direction
  const sCosPhi = h[5].mul(2.0).sub(1.0);
  const sSinPhi = sCosPhi.mul(sCosPhi).oneMinus().max(0.0).sqrt();
  const sphereDir = vec3(sSinPhi.mul(theta.cos()), sSinPhi.mul(theta.sin()), sCosPhi).normalize();

  // hemisphere: upper
  const hCos = h[5];
  const hSin = hCos.mul(hCos).oneMinus().max(0.0).sqrt();
  const hemiDir = vec3(hSin.mul(theta.cos()), hCos, hSin.mul(theta.sin())).normalize();

  const code = u.shape;
  return float(code).equal(float(GpuShapeCode.Cone)).select(
    coneDir,
    float(code).equal(float(GpuShapeCode.Sphere)).select(
      sphereDir,
      float(code).equal(float(GpuShapeCode.Hemisphere)).select(
        hemiDir,
        vec3(0.0, 1.0, 0.0), // default up (point/box/circle)
      ),
    ),
  );
}

// ─── Kernel factory ──────────────────────────────────────────────────────────

export interface CreateKernelsParams {
  /** Power-of-two capacity (slot count). */
  capacity: number;
  /**
   * Optional SDF collision (2026-08-17 audit gap): particles sample the
   * scene's signed-distance volume (see render/gi/sdf/SdfGpu) and are pushed
   * out along the finite-difference gradient with a velocity reflect+damp.
   * Compile-time optional — omitting it costs nothing in the kernel.
   */
  collisionSdf?: {
    volume: THREE.Data3DTexture;
    /** World min corner of the volume. */
    origin: [number, number, number];
    /** World size of the volume. */
    size: [number, number, number];
    /** Velocity kept along the reflected normal 0..1 (default 0.4). */
    bounce?: number;
    /** Tangential velocity kept 0..1 (default 0.85). */
    friction?: number;
    /** Collision radius in world units (default 0.05). */
    radius?: number;
  };
}

export function createParticleKernels(params: CreateKernelsParams): ParticleKernels {
  const { capacity } = params;
  // Over-allocate one slot for the r182 instancedArray.bufferCount off-by-one.
  const slots = capacity + 1;

  const posBuffer = instancedArray(slots, 'vec4');
  const velBuffer = instancedArray(slots, 'vec4');
  const miscBuffer = instancedArray(slots, 'vec4');

  // ── Spawn uniforms ──
  const spawnUniforms: SpawnUniforms = {
    spawnStart: uniform(0, 'uint'),
    spawnCount: uniform(0, 'uint'),
    frameSeed: uniform(0, 'uint'),
    originX: uniform(0), originY: uniform(0), originZ: uniform(0),
    // Identity until an emitter uploads its own basis.
    basis0X: uniform(1), basis0Y: uniform(0), basis0Z: uniform(0),
    basis1X: uniform(0), basis1Y: uniform(1), basis1Z: uniform(0),
    basis2X: uniform(0), basis2Y: uniform(0), basis2Z: uniform(1),
    shape: uniform(GpuShapeCode.Point),
    shapeRadius: uniform(1), shapeHeight: uniform(1), shapeAngle: uniform(Math.PI / 6),
    boxX: uniform(1), boxY: uniform(1), boxZ: uniform(1),
    speedMin: uniform(1), speedMax: uniform(1),
    lifeMin: uniform(2), lifeMax: uniform(2),
    sizeMin: uniform(0.1), sizeMax: uniform(0.1),
  };

  // ── Update uniforms ──
  const updateUniforms: UpdateUniforms = {
    gravity: uniform(0),
    drag: uniform(0),
    curlStrength: uniform(0),
    curlFrequency: uniform(1),
    curlScroll: uniform(0),
    attractorX: uniform(0), attractorY: uniform(0), attractorZ: uniform(0),
    attractorStrength: uniform(0),
    time: uniform(0),
  };

  // ── Clear kernel: zero every slot, park far away, dead (age >= life=1). ──
  const clearNode = Fn(() => {
    posBuffer.element(instanceIndex).assign(vec4(0.0, GPU_PARTICLE_DEAD_Y, 0.0, 2.0)); // age=2 > life
    velBuffer.element(instanceIndex).assign(vec4(0.0, 0.0, 0.0, 0.0)); // size 0
    miscBuffer.element(instanceIndex).assign(vec4(1.0, 0.0, 0.1, 1.0)); // life=1
  })().compute(slots);

  // ── Spawn kernel ──
  // Dispatched with count = spawnCount; the global instanceIndex maps to slot
  // (spawnStart + instanceIndex). Each thread initialises exactly one slot.
  const spawnNode = Fn(() => {
    const slot = uint(spawnUniforms.spawnStart).add(instanceIndex);
    const seed = slot.add(uint(spawnUniforms.frameSeed));

    // Six independent [0,1) draws.
    const h0 = hash(seed);
    const h1 = hash(seed.add(uint(101)));
    const h2 = hash(seed.add(uint(211)));
    const h3 = hash(seed.add(uint(307)));
    const h4 = hash(seed.add(uint(401)));
    const h5 = hash(seed.add(uint(509)));
    const hSpeed = hash(seed.add(uint(601)));
    const hLife = hash(seed.add(uint(701)));
    const hSize = hash(seed.add(uint(809)));
    const draws = [h0, h1, h2, h3, h4, h5];

    const localPos = sampleShapePos(spawnUniforms, draws);
    const dir = sampleShapeDir(spawnUniforms, draws);

    const speed = float(spawnUniforms.speedMin)
      .add(float(spawnUniforms.speedMax).sub(float(spawnUniforms.speedMin)).mul(hSpeed));
    const life = float(spawnUniforms.lifeMin)
      .add(float(spawnUniforms.lifeMax).sub(float(spawnUniforms.lifeMin)).mul(hLife));
    const size = float(spawnUniforms.sizeMin)
      .add(float(spawnUniforms.sizeMax).sub(float(spawnUniforms.sizeMin)).mul(hSize));

    // World-space placement: rotate the emitter-local sample into the emitter's
    // basis, THEN translate. Columns of the upper 3×3, so this is basis·local.
    const b0 = vec3(float(spawnUniforms.basis0X), float(spawnUniforms.basis0Y), float(spawnUniforms.basis0Z));
    const b1 = vec3(float(spawnUniforms.basis1X), float(spawnUniforms.basis1Y), float(spawnUniforms.basis1Z));
    const b2 = vec3(float(spawnUniforms.basis2X), float(spawnUniforms.basis2Y), float(spawnUniforms.basis2Z));
    const rotated = b0.mul(localPos.x).add(b1.mul(localPos.y)).add(b2.mul(localPos.z));
    const worldPos = vec3(
      rotated.x.add(float(spawnUniforms.originX)),
      rotated.y.add(float(spawnUniforms.originY)),
      rotated.z.add(float(spawnUniforms.originZ)),
    );
    // Direction is rotated and RE-NORMALIZED (the CPU path's transformDirection
    // normalizes too), so a scaled emitter does not change particle speed.
    const worldDir = b0.mul(dir.x).add(b1.mul(dir.y)).add(b2.mul(dir.z)).normalize();
    const vel = worldDir.mul(speed);

    posBuffer.element(slot).assign(vec4(worldPos, float(0.0))); // age 0
    velBuffer.element(slot).assign(vec4(vel, size));
    // seed stored for stable per-particle color/size jitter in the material.
    miscBuffer.element(slot).assign(vec4(life, hSize, size, speed));
  })().compute(1); // count overwritten per-dispatch by GpuParticlePool

  // ── Update kernel ──
  const updateNode = Fn(() => {
    const idx = instanceIndex;
    const dt = deltaTime;
    const pos4 = posBuffer.element(idx);
    const vel4 = velBuffer.element(idx);
    const misc4 = miscBuffer.element(idx);

    const age = pos4.w.add(dt);
    const life = misc4.x;

    If(age.greaterThanEqual(life), () => {
      // Dead — park far away, size 0 → degenerate quad in the vertex stage.
      pos4.assign(vec4(0.0, float(GPU_PARTICLE_DEAD_Y), 0.0, life.add(1.0)));
      vel4.w.assign(0.0);
    }).Else(() => {
      const p = vec3(pos4.x, pos4.y, pos4.z);
      const v = vec3(vel4.x, vel4.y, vel4.z).toVar();

      // Gravity (Y only; gravity uniform already includes ×-9.81 sign).
      v.y.addAssign(float(updateUniforms.gravity).mul(dt));

      // Linear drag: v *= exp(-drag*dt) ≈ (1 - drag*dt) clamped.
      const dragFactor = float(updateUniforms.drag).mul(dt).oneMinus().max(0.0);
      v.assign(v.mul(dragFactor));

      // Curl-noise turbulence — sample at scaled, time-scrolled position.
      const f = float(updateUniforms.curlFrequency);
      const tScroll = float(updateUniforms.time).mul(float(updateUniforms.curlScroll));
      const samplePos = p.mul(f).add(vec3(tScroll, tScroll, tScroll));
      const curl = curlNoiseTSL(samplePos.x, samplePos.y, samplePos.z);
      v.addAssign(curl.mul(float(updateUniforms.curlStrength)).mul(dt));

      // Attractor — accel toward point, 1/(d²+ε) falloff.
      const toAtt = vec3(
        float(updateUniforms.attractorX).sub(p.x),
        float(updateUniforms.attractorY).sub(p.y),
        float(updateUniforms.attractorZ).sub(p.z),
      );
      const d2 = toAtt.dot(toAtt).add(0.001);
      const attAccel = toAtt.div(d2.sqrt()).mul(float(updateUniforms.attractorStrength).div(d2));
      v.addAssign(attAccel.mul(dt));

      // Integrate position.
      const newPos = p.add(v.mul(dt)).toVar();

      // SDF collision (compile-time optional): push out along the gradient,
      // reflect the normal velocity (×bounce), damp the tangential (×friction).
      if (params.collisionSdf) {
        const c = params.collisionSdf;
        const inv = vec3(1 / c.size[0], 1 / c.size[1], 1 / c.size[2]);
        const org = vec3(c.origin[0], c.origin[1], c.origin[2]);
        const sdfAt = (qIn: unknown): ReturnType<typeof float> => {
          const q = qIn as ReturnType<typeof vec3>;
          const uvw = q.sub(org).mul(inv).clamp(vec3(0, 0, 0), vec3(1, 1, 1));
          return float(texture3D(c.volume, uvw).r);
        };
        const radius = float(c.radius ?? 0.05);
        const d = sdfAt(newPos);
        If(d.lessThan(radius), () => {
          const eps = 0.15;
          const nrm = vec3(
            sdfAt(newPos.add(vec3(eps, 0, 0))).sub(d),
            sdfAt(newPos.add(vec3(0, eps, 0))).sub(d),
            sdfAt(newPos.add(vec3(0, 0, eps))).sub(d),
          ).normalize();
          newPos.assign(newPos.add(nrm.mul(radius.sub(d))));
          const vn = nrm.mul(v.dot(nrm));
          const vt = v.sub(vn);
          v.assign(vt.mul(c.friction ?? 0.85).sub(vn.mul(c.bounce ?? 0.4)));
        });
      }

      pos4.assign(vec4(newPos, age));
      vel4.x.assign(v.x);
      vel4.y.assign(v.y);
      vel4.z.assign(v.z);
    });
  })().compute(slots);

  return {
    posBuffer,
    velBuffer,
    miscBuffer,
    clearNode,
    spawnNode,
    updateNode,
    spawnUniforms,
    updateUniforms,
  };
}
